import { USER_GUIDE_QUICK_START_URL } from "@/constants/user-guide";

import type { Step } from "./steps";

const TRIAL_BALANCE_CONVERSION_GUIDE_URL = "https://youtu.be/7Rt_wVCjkkY";
const DSD_IXD_COMPARE_GUIDE_URL = "https://youtu.be/UGcyIWRW2PM";
const DSD_REVIEW_GUIDE_URL = "https://youtu.be/wcBy7OFn77g";
const DSD_DSD_COMPARE_GUIDE_URL = "https://youtu.be/WzcjiZuLvFo";

export type Faq = {
  question: string;
  answer: string;
  /**
   * 이용 가이드 바로가기. 없으면 카드를 그리지 않는다.
   * kind 는 이동할 곳의 종류다 — 노션 이용가이드(`page`)와 유튜브 영상(`video`)을 클릭 로그에서 따로 센다
   */
  guide?: { label: string; url: string; kind: "page" | "video" };
  /** 답변 CTA 가 여는 홈 STEP 패널 */
  cta: { label: string; stepId: Step["id"] };
};

// ponytail: 정해진 답변만 라우팅한다. 실제 AI 는 /help(AI 상담) 페이지 몫
// 노션 가이드 카드는 처음 쓰는 사람용 질문이라 이용가이드의 빠른 시작 페이지로 보낸다
export const FAQS: readonly Faq[] = [
  {
    question: "처음인데 어떤 기능부터 사용하면 되나요?",
    answer:
      "파일 변환부터 바로 시작하셔도 됩니다.\n업로드 화면에서 정산표 또는 이전 DSD 파일을 선택하시면 그에 맞는 파일로 변환해 드립니다.\n\n흐름이 낯설다면 이용 가이드를 먼저 확인해 보세요.\n파일 변환부터 태깅, 검증, 제출까지 전체 흐름을 한눈에 보실 수 있습니다.",
    guide: {
      label: "이용 가이드 보러가기",
      url: USER_GUIDE_QUICK_START_URL,
      kind: "page",
    },
    cta: { label: "파일 변환하기", stepId: "base-report" },
  },
  {
    question: "정산표만 있는데 어떻게 하나요?",
    answer:
      "정산표만 있어도 바로 시작하실 수 있습니다.\n\n업로드 화면에서 정산표 탭을 선택하고 파일을 올려 보세요. 양식에 맞춰 옮겨 쓸 필요없이 정산표를 DSD 형식에 맞게 변환해 드립니다.",
    guide: {
      label: "정산표 변환 방법",
      url: TRIAL_BALANCE_CONVERSION_GUIDE_URL,
      kind: "video",
    },
    cta: { label: "파일 변환하기", stepId: "base-report" },
  },
  {
    question: "작성한 DSD에 오류가 없는지 어떻게 확인하나요?",
    answer:
      "제출 전에 검증을 한 번 실행해 보세요.\n\n합계 불일치, 오탈자 등 본문과 주석 사이 연동되는 값의 오류까지 한 번에 찾아 드립니다.\n\n결과 목록에서 항목을 누르시면 표 위치로 바로 이동하니, 위에서부터 순서대로 수정이 가능합니다.",
    guide: {
      label: "이용 가이드 보러가기",
      url: DSD_REVIEW_GUIDE_URL,
      kind: "video",
    },
    cta: { label: "DSD 리뷰하기", stepId: "dsd-review" },
  },
  {
    question: "DSD 버전 비교는 어떻게 확인하나요?",
    answer:
      "버전 비교로 확인해 보세요.\n감사 전 파일과 수정 후 파일을 선택하시면\n달라진 계정과목과 금액을 하이라이트로 표시해 드립니다.\n\n수정할 때는 값만 빠르게 채워 넣으셔도 되고,\n워크북을 나란히 띄워 대조하며 값을 채울 수도 있습니다.\n\n어떤 항목이 얼마나 바뀌었는지 확인하고\n보고 및 협업을 위해 html 파일로 저장하고 공유해보세요.",
    guide: {
      label: "이용 가이드 보러가기",
      url: DSD_DSD_COMPARE_GUIDE_URL,
      kind: "video",
    },
    cta: { label: "DSD - DSD 대사하기", stepId: "dsd-compare" },
  },
  {
    question: "이번 시즌 XBRL 작업은 어디서부터 시작해야하나요?",
    answer:
      "이전에 해두신 태깅을 그대로 불러올 수 있습니다.\n\n전기나 직전 분기 파일을 선택하시면 전기 XBRL 태그를 불러와 동일한 계정과목에 자동으로 태깅해 드립니다.\n새로 생기거나 바뀐 계정만 확인해 새롭게 매핑해주세요.\n\n한 번 저장해 두면 다음 기수에 버전으로 관리가 가능해요.",
    cta: { label: "XBRL 태그 불러오기", stepId: "xbrl-tagging" },
  },
  {
    question: "태깅한 항목에 값은 어떻게 채우나요?",
    answer:
      "DSD 워크북 파일을 선택하고 IXD 파일을 올려 보세요.\n두 파일의 항목을 나란히 놓고 값을 비교해 드립니다.\n\n어긋난 항목은 그 화면에서 바로 수정하실 수 있고, DSD 워크북의 값을 그대로 채워 넣을 수 있어요.\n\n비어 있는 항목도 함께 표시되니 순서대로 채워 넣어보세요.",
    cta: { label: "IXD 값 채우기", stepId: "ixd-value-input" },
  },
  {
    question: "제출 전에 숫자가 맞는지 확인하려면 무엇을 하나요?",
    answer:
      "DSD - IXD 대사로 보고서 최종 검토를 마무리해주세요.\n본문과 주석을 맞대어 일치/불일치 항목을 한 눈에 보고,\n단위 차이까지 함께 확인할 수 있어요.\n\n모든 값을 확인하고 수정까지 완료되면\n새로운 IXD파일로 다운로드가 가능합니다.",
    guide: {
      label: "DSD-IXD 대사 방법 확인",
      url: DSD_IXD_COMPARE_GUIDE_URL,
      kind: "video",
    },
    cta: { label: "최종 검토하기", stepId: "final-review" },
  },
];
